import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { CHAPTER_THEME } from "./serviceTheme";

// "Lo que hacemos" — three cards with a skewed-gradient-panel reveal.
// Structure borrowed from a reference "skew card" hover effect (gradient
// panel + blurred twin + floating glass blobs behind a glass content
// panel); recolored entirely into Malinois's own signal colors (single
// hue per card, deepening toward ink — no neon two-color combos) and
// squared off (no rounded corners) to match the site's sharp, editorial
// look instead of the reference's soft glassmorphism.
// Desktop (real hover): mouse enter/leave drives the reveal, one card
// active at a time. Mobile/tablet (touch, no hover): there's no cursor to
// hover, so a +/- toggle button appears on each card instead — tap + to
// expand that card, tap - to close it, independent per card.
// Text stays paper/white at all times — legibility on activation comes
// from the content panel itself going opaque dark (not from swapping
// text to ink), so the bright signal-color panel behind it never has to
// fight with the copy. Title/number/body use the modern sans (font-body)
// instead of the editorial serif for this section specifically.
const COLOR_BY_INDEX = ["cobalt", "lime", "orange"];

const CHAPTER_GRADIENT = {
  cobalt: { from: "#5A64FF", to: "#1D2266" }, // Voz de Marca
  lime: { from: "#B7F33D", to: "#3C5A0E" }, // Conversión
  orange: { from: "#FF6433", to: "#7A2E1E" }, // Video
};

// True on devices with a real mouse/trackpad. Touch tablets fall on the
// "no hover" side regardless of screen width, same distinction used for
// the founder-photo easter egg.
function useHasHover() {
  const [hasHover, setHasHover] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setHasHover(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return hasHover;
}

function WhatWeDoCard({ service, i, tone, hasHover, hoveredIndex, setHoveredIndex }) {
  const [tapExpanded, setTapExpanded] = useState(false);

  const isLight = tone === "light";
  const inactiveText = isLight ? "text-ink" : "text-paper";
  const inactiveTextMuted = isLight ? "text-ink/45" : "text-paper/50";
  const inactiveTextBody = isLight ? "text-ink/70" : "text-paper/80";
  const inactiveCardBg = isLight ? "bg-ink/[0.03]" : "bg-paper/[0.04]";
  const inactiveCardBorder = isLight ? "border-ink/10" : "border-paper/10";

  const colorKey = service.colorKey || COLOR_BY_INDEX[i % 3];
  const theme = CHAPTER_THEME[colorKey];
  const grad = CHAPTER_GRADIENT[colorKey];
  const isActive = hasHover ? hoveredIndex === i : tapExpanded;

  return (
    <div
      onMouseEnter={hasHover ? () => setHoveredIndex(i) : undefined}
      onMouseLeave={hasHover ? () => setHoveredIndex((a) => (a === i ? null : a)) : undefined}
      className={`relative transition-[height] duration-500 ease-out ${
        hasHover ? "cursor-pointer" : ""
      } ${isActive ? "h-[560px] md:h-[600px]" : "h-[380px] md:h-[420px]"}`}
      data-testid={`whatwedo-block-${service.number}`}
    >
      {/* Skewed gradient panel — unskews and widens toward full-bleed on activation */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute top-0 h-full transition-all duration-500 ease-out ${
          isActive ? "left-[16px] w-[calc(100%-32px)] skew-x-0" : "left-[46px] w-1/2 skew-x-[15deg]"
        }`}
        style={{ background: `linear-gradient(315deg, ${grad.from}, ${grad.to})` }}
      />
      {/* Blurred twin of the same panel — the glow */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute top-0 h-full blur-[28px] opacity-70 transition-all duration-500 ease-out ${
          isActive ? "left-[16px] w-[calc(100%-32px)] skew-x-0" : "left-[46px] w-1/2 skew-x-[15deg]"
        }`}
        style={{ background: `linear-gradient(315deg, ${grad.from}, ${grad.to})` }}
      />

      {/* Floating glass blobs — soft paper-tinted, no neon */}
      <span
        aria-hidden="true"
        className={`card-blob pointer-events-none absolute rounded-full bg-paper/10 backdrop-blur-[10px] transition-all duration-500 ${
          isActive ? "top-[-24px] left-[36px] w-16 h-16 opacity-100" : "top-0 left-0 w-0 h-0 opacity-0"
        }`}
      />
      <span
        aria-hidden="true"
        className={`card-blob card-blob-delay pointer-events-none absolute rounded-full bg-paper/10 backdrop-blur-[10px] transition-all duration-500 ${
          isActive ? "bottom-[-24px] right-[36px] w-16 h-16 opacity-100" : "bottom-0 right-0 w-0 h-0 opacity-0"
        }`}
      />

      {/* Content — glass panel, shifts left and gains room on activation.
          Legibility on hover comes from THIS panel going opaque dark
          (bg-ink/90), not from the text swapping color — text stays
          paper/white in both states. */}
      <div
        className={`relative z-10 h-full flex flex-col border backdrop-blur-[10px] transition-all duration-500 ease-out ${
          isActive
            ? "left-[-14px] px-8 py-9 md:px-9 md:py-10 bg-ink/90 border-paper/10"
            : `left-0 px-7 py-7 md:px-8 md:py-8 ${inactiveCardBg} ${inactiveCardBorder}`
        }`}
      >
        {/* No-hover devices only — desktop keeps hover as the only
            trigger, matching the CTA-less "browse" feel of the rest of
            the card grid. */}
        {!hasHover && (
          <button
            type="button"
            onClick={() => setTapExpanded((v) => !v)}
            aria-label={isActive ? "Cerrar" : "Expandir"}
            aria-expanded={isActive}
            data-testid={`whatwedo-toggle-${service.number}`}
            className={`absolute top-6 right-6 md:top-7 md:right-7 flex items-center justify-center h-8 w-8 rounded-full border transition-colors duration-300 ${
              isActive
                ? "border-paper/30 text-paper"
                : `${inactiveCardBorder} ${inactiveText}`
            }`}
          >
            {isActive ? <Minus size={16} /> : <Plus size={16} />}
          </button>
        )}

        <span
          className={`font-body text-xs tracking-[0.25em] transition-colors duration-300 ${
            isActive ? "text-paper/50" : inactiveTextMuted
          }`}
        >
          {service.number}
        </span>
        <h3
          className={`mt-3 font-body font-semibold text-2xl md:text-3xl leading-snug [text-wrap:balance] transition-colors duration-300 ${
            isActive ? "text-paper" : inactiveText
          } ${!hasHover ? "pr-10" : ""}`}
        >
          {service.title}
        </h3>
        <p
          className={`mt-4 font-body text-base leading-relaxed [text-wrap:pretty] transition-colors duration-300 ${
            isActive ? "text-paper/80" : inactiveTextBody
          }`}
        >
          {service.text}
        </p>

        <AnimatePresence initial={false}>
          {isActive && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 overflow-hidden space-y-2"
              data-testid={`whatwedo-deliverables-${service.number}`}
            >
              {service.deliverables?.map((d, j) => (
                <motion.li
                  key={d}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: j * 0.05 }}
                  className="flex items-center gap-2.5 font-body text-sm text-paper/85"
                >
                  <span className="h-1 w-1 shrink-0 rounded-full bg-paper/50" aria-hidden="true" />
                  {d}
                </motion.li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// tone="dark" (default) — card sits on a bg-ink section, inactive state
//   uses paper/white text on a near-invisible paper-tinted panel.
// tone="light" — card sits on a bg-paper section; inactive state flips to
//   ink text on an ink-tinted panel so it doesn't disappear. The active
//   (hover/tap) state is untouched in both tones — it already carries its
//   own opaque bg-ink/90 overlay, so text-paper stays legible regardless
//   of what's behind the card.
export function WhatWeDoBlocks({ services = [], tone = "dark" }) {
  const hasHover = useHasHover();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12" data-testid="whatwedo-blocks">
      {services.map((service, i) => (
        <WhatWeDoCard
          key={service.number}
          service={service}
          i={i}
          tone={tone}
          hasHover={hasHover}
          hoveredIndex={hoveredIndex}
          setHoveredIndex={setHoveredIndex}
        />
      ))}
    </div>
  );
}
